import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useSelector } from 'react-redux';

import CodeSubmitButton from '../../../../components/CodeSubmitButton/CodeSubmitButton';
import alyceGiftPointer from '../../assets/images/gift-pointer.svg';
import SupportLink from '../../../../components/Shared/SupportLink';
import { validationSchema } from '../../store/code.schemas';
import { getIsLoading } from '../../store/code.selectors';
import AlycePrivacy from '../../../../components/AlycePrivacy/AlycePrivacy';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    height: 'calc(100vh - 85px)',
    minHeight: 'max-content',
  },
  formWrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: 500,
  },
  headerTitle: {
    fontSize: 32,
    lineHeight: 1.31,
    marginTop: spacing(3),
    marginBottom: 40,
    color: palette.brown.main,
    marginRight: spacing(1),
  },
  codeButton: {
    fontWeight: 'bold',
    fontStyle: 'normal',
    fontStretch: 'normal',
    letterSpacing: 'normal',
    lineHeight: 1.5,
  },
  helperMessage: {
    marginTop: spacing(6),
    fontSize: 16,
    fontWeight: 'bold',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    textAlign: 'center',
    color: palette.brown.secondary,
  },
  supportLink: {
    display: 'inline-block',
    textDecoration: 'none',
  },
}));

export interface IRecipientCodeForm {
  onSubmit: (code: string) => void;
}

const RecipientCodeForm = ({ onSubmit }: IRecipientCodeForm): JSX.Element => {
  const classes = useStyles();

  const isLoading = useSelector(getIsLoading);

  const onSubmitForm = useCallback(
    data => {
      onSubmit(data.code);
    },
    [onSubmit],
  );

  const { errors, handleSubmit, control } = useForm({
    mode: 'all',
    defaultValues: {
      code: '',
    },
    resolver: yupResolver(validationSchema),
  });

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Box className={classes.root} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
        <Box className={classes.formWrapper} mx="auto" my={0}>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography className={classes.headerTitle}>Unwrap your gift</Typography>
            <img src={alyceGiftPointer} alt="Alyce" />
          </Box>

          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item xs={12}>
              <Controller
                control={control}
                name="code"
                render={({ onChange, value }) => (
                  <TextField
                    data-testid="giftCodeInputField"
                    name="code"
                    label="Enter your gift code"
                    placeholder="Enter your gift code"
                    fullWidth
                    variant="outlined"
                    value={value}
                    disabled={isLoading}
                    error={!!errors.code}
                    helperText={errors.code && errors.code.message}
                    onChange={onChange}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <CodeSubmitButton isDisabled={isLoading} />
            </Grid>
          </Grid>

          <Grid container display="flex" justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Typography className={classes.helperMessage}>
                Have questions? <SupportLink />
              </Typography>
            </Grid>
          </Grid>
          <AlycePrivacy />
        </Box>
      </Box>
    </form>
  );
};

RecipientCodeForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default memo(RecipientCodeForm);
